import { ProfileData } from "@/types/profile";
import { User } from "@supabase/supabase-js";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Settings as SettingsIcon, 
  Camera, 
  Star, 
  Calendar,
  MapPin,
  Verified,
  Crown,
  Sparkles
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";

interface ModernProfileHeaderProps {
  profile: ProfileData | null;
  user: User | null;
}

const ModernProfileHeader = ({ profile, user }: ModernProfileHeaderProps) => {
  const navigate = useNavigate();

  const memberSince = user?.created_at 
    ? format(new Date(user.created_at), 'MMM yyyy')
    : 'N/A';

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative"
    >
      <Card className="overflow-hidden relative border-0 bg-gradient-to-br from-primary/5 via-background/80 to-secondary/10 backdrop-blur-xl border border-white/10 shadow-2xl">
        {/* Premium background effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10 opacity-50"></div>
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent"></div>
        
        <div className="relative">
          {/* Hero section with gradient overlay */}
          <div className="h-40 bg-gradient-to-r from-primary/20 via-primary/10 to-secondary/20 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background/20"></div>
            
            {/* Floating particles effect */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 bg-primary/20 rounded-full"
                  animate={{
                    y: [-10, -100],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 3 + i * 0.5,
                    repeat: Infinity,
                    delay: i * 0.4,
                  }}
                  style={{
                    left: `${10 + i * 12}%`,
                    top: '100%',
                  }}
                />
              ))}
            </div>
          </div>

          <div className="p-8 -mt-20 relative z-10">
            {/* Main profile section */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8">
              {/* Enhanced Avatar */}
              <motion.div 
                className="relative group"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
              >
                <div className="relative">
                  <Avatar className="h-32 w-32 ring-4 ring-background shadow-2xl">
                    <AvatarImage src={profile?.avatar_url || ''} />
                    <AvatarFallback className="text-2xl font-bold bg-gradient-to-br from-primary to-secondary text-white">
                      {profile?.full_name ? getInitials(profile.full_name) : 'U'}
                    </AvatarFallback>
                  </Avatar>
                  
                  {/* Premium badge */}
                  <div className="absolute -bottom-2 -right-2">
                    <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg">
                      <Crown className="h-3 w-3 mr-1" />
                      VIP
                    </Badge>
                  </div>
                  
                  {/* Upload overlay */}
                  <motion.div 
                    className="absolute inset-0 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Camera className="h-8 w-8 text-white" />
                  </motion.div>
                </div>
              </motion.div>

              {/* Profile Information */}
              <div className="flex-1 space-y-6">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="space-y-3"
                >
                  <div className="flex items-center gap-3">
                    <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                      {profile?.full_name || 'Welcome!'}
                    </h1>
                    <Verified className="h-6 w-6 text-blue-500" />
                  </div>
                  
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span className="text-lg">{profile?.email || user?.email}</span>
                    <Badge variant="secondary" className="bg-green-100 text-green-800">
                      Verified
                    </Badge>
                  </div>
                  
                  {profile?.bio && (
                    <p className="text-muted-foreground leading-relaxed max-w-2xl">
                      {profile.bio}
                    </p>
                  )}
                </motion.div>

                {/* Stats & Info Grid */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="grid grid-cols-2 lg:grid-cols-4 gap-4"
                >
                  <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      <span className="text-xs text-muted-foreground">Member Since</span>
                    </div>
                    <p className="font-semibold">{memberSince}</p>
                  </div>
                  
                  <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="h-4 w-4 text-amber-500" />
                      <span className="text-xs text-muted-foreground">Status</span>
                    </div>
                    <p className="font-semibold text-amber-600">Premium</p>
                  </div>
                  
                  <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="h-4 w-4 text-green-500" />
                      <span className="text-xs text-muted-foreground">Location</span>
                    </div>
                    <p className="font-semibold">{profile?.location || 'Not set'}</p>
                  </div>
                  
                  <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="h-4 w-4 text-purple-500" />
                      <span className="text-xs text-muted-foreground">Activity</span>
                    </div>
                    <p className="font-semibold text-green-600">Active</p>
                  </div>
                </motion.div>
              </div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col gap-3"
              >
                <Button 
                  variant="default" 
                  className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg"
                  onClick={() => navigate('/settings/profile')}
                >
                  <SettingsIcon className="h-4 w-4 mr-2" />
                  Edit Profile
                </Button>
                
                <Button 
                  variant="outline"
                  className="border-border/50 bg-card/50 backdrop-blur-sm hover:bg-card/80"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Change Photo
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default ModernProfileHeader;